import Cookies from "js-cookie";
import pick from "lodash/pick";
import get from "lodash/get";

const userCookieParams = [
  "authenticated",
  "email",
  "id",
  "firstName",
  "lastName",
  "phone",
  "token",
  "avatarId",
  "expires",
];

const defaultConfig = Object.freeze({
  domain: process.env.NEXT_PUBLIC_DOMAIN,
});

const cookieActions = (cookieName, additionalConfig) => {
  const cookieConfig = {
    ...defaultConfig,
    ...additionalConfig,
  };
  return {
    set: (value) => {
      Cookies.set(cookieName, JSON.stringify(value), cookieConfig);
    },
    get: () => {
      const value = Cookies.get(cookieName);
      if (value) {
        return JSON.parse(value);
      }
      return value;
    },
    reset: () => Cookies.remove(cookieName),
  };
};

// ToDo: Eshop - Pass this from above
const userCookieName = `users#${process.env.NEXT_PUBLIC_ENVIRONMENT}`;

export const userCookie = {
  ...cookieActions(userCookieName),
  set: (value) => {
    const cookieValues = pick(value, userCookieParams);
    Cookies.set(userCookieName, JSON.stringify(cookieValues), defaultConfig);
  },
};

export const getCookieByName = (cname) => {
  if (typeof document === "undefined") {
    return null;
  }
  const allCookies = document.cookie.split("; ").reduce((prev, current) => {
    const [name, value] = current.split("=");
    prev[name] = value;
    return prev;
  }, {});
  return get(allCookies, cname);
};

export const getJsonCookie = (name) => {
  const ck = decodeURIComponent(getCookieByName(name));
  function JSONCookie(str) {
    if (typeof str !== "string" || str.substr(0, 2) !== "j:") {
      return undefined;
    }

    try {
      return JSON.parse(str.slice(2));
    } catch (err) {
      return undefined;
    }
  }
  return JSONCookie(ck);
};

/**
 * Extracts client IP from the geo cookie (set by geolocation middleware).
 * Used by LaunchDarkly client-side wrappers for IP-based targeting.
 */
export const getClientIpFromGeoCookie = () => {
  const geo = getJsonCookie("geo");
  return geo?.ip_address ?? null;
};

export const calculateCookieExpiryTime = (time) => {
  const [rawNum] = time.split(/m|y/);
  const isMonth = time.endsWith("m");
  const t = new Date();
  const num = Number(rawNum);
  if (isMonth) {
    t.setMonth(t.getMonth() + num);
  } else {
    t.setFullYear(t.getFullYear() + num);
  }
  return t.toUTCString();
};

export const setCookie = (cname, cvalue, options = {}) => {
  const { time, domain } = options || {};

  let t = new Date();
  t.setFullYear(t.getFullYear() + 1);

  const expirationDate = time || t.toUTCString();
  const domainToBeAdded = domain ? `domain=${domain};` : "";
  document.cookie = `${cname}=${cvalue};${domainToBeAdded}path=/;expires=${expirationDate};`;
};

export const removeCookie = (cookieName, path, domain = "") =>
  Cookies.remove(cookieName, { path: path, ...(domain && { domain }) });

export const getCookie = (cname) => Cookies.get(cname);

const SELECTED_WORKSPACE_SLUG_COOKIE_NAME = "selectedWorkspaceSlug";

export const selectedWorkspaceSlugCookie = {
  set: (value) => {
    // Use { path: "/" } without an explicit domain so the cookie is scoped to the
    // current host — matching the way Next.js middleware sets the same cookie.
    // Using defaultConfig here would add domain=NEXT_PUBLIC_DOMAIN which on localhost
    // includes a port (invalid for cookies) and on production may differ from the
    // middleware's implicit domain, causing the browser to silently drop the update.
    Cookies.set(SELECTED_WORKSPACE_SLUG_COOKIE_NAME, value, { path: "/" });
  },
  get: () => {
    return Cookies.get(SELECTED_WORKSPACE_SLUG_COOKIE_NAME);
  },
  reset: () => Cookies.remove(SELECTED_WORKSPACE_SLUG_COOKIE_NAME, { path: "/" }),
};
